import { AnyTaggedItem } from "src/types";
import { db } from "../dexie";
import { assertExists } from "../utils/assertExists";
import { Habit, habitRecordSchema, Task } from "../types";
import { Sync } from "../sync";
import { Ordering } from "../models/Ordering";
import { buildInstanceOverride } from "../models/InstanceOverrides";
import { HabitInstantiator } from "../models/HabitInstantiator";
import { Habits } from "../services/Habits";
import { Tasks } from "../services/Tasks";

/** Keep `isProject` and the `project` component pill in sync for tasks. */
export function alignTaskProjectPillWithFlag(task: Task): Task {
  const hasPill = task.components.includes("project");
  if (task.isProject && !hasPill) {
    return { ...task, components: [...task.components, "project"] };
  }
  if (!task.isProject && hasPill) {
    return { ...task, components: task.components.filter((c) => c !== "project") };
  }
  return task;
}

export async function moveItem(item: AnyTaggedItem, parent: AnyTaggedItem | null, order: number) {
  if (parent && item.type !== parent.type) {
    throw new Error("Cannot move item to a parent of a different type");
  }

  const { id: itemId, type } = item;
  const parentId = parent ? parent.id : null;

  const siblings = await (async (): Promise<AnyTaggedItem[]> => {
    switch (type) {
      case "task": {
        return Tasks.getByParentId(parentId)
          .filter((task) => task.id !== itemId)
          .toArray();
      }
      case "habit": {
        return Habits.getByParentId(parentId)
          .filter((habit) => habit.id !== itemId)
          .toArray();
      }
      case "instance": {
        const items = await HabitInstantiator.getInstancesByDate(item.date);
        return items.filter((instance) => instance.parentId === parentId && instance.id !== itemId);
      }
    }
  })();

  Ordering.sortInPlace(siblings, (item) => item.order);
  const newOrder = Ordering.insertAt(siblings, (item) => item.order, order);

  await updateItem({ ...item, order: newOrder, parentId });
}

export async function updateItem(item: AnyTaggedItem) {
  if (item.type === "instance") {
    await db.transaction("rw", [...Sync.tables(db.instances), db.habits], async () => {
      const habitRecord = await assertExists(item.templateId, db.habits);
      const habit = habitRecordSchema.encode(habitRecord);

      await Sync.upsert(buildInstanceOverride(item, habit));
    });
  } else {
    await Sync.upsert(item.type === "task" ? alignTaskProjectPillWithFlag(item) : item);
  }
}

export async function createItem(item: Task | Habit) {
  await Sync.upsert(item.type === "task" ? alignTaskProjectPillWithFlag(item) : item);
}

export async function deleteItem(item: AnyTaggedItem) {
  await Sync.delete(item);
}
